// src/MainApp.js
import React, { useRef, useState, useEffect, useCallback } from 'react';
import './App.css';
import Sidebar from './components/Sidebar';
import TopNavbar from './components/TopNavbar';
import CategorySelector from './components/CategorySelector';
import ProjectsGrid from './components/ProjectsGrid';
import PhoneHeader from './components/PhoneHeader';
import PhoneMenu from './components/PhoneMenu';

// Terms for category filtering
  const CATEGORY_TERMS = {
    luxury:          ['lux', 'luxury'],
    automotive:      ['auto', 'autos', 'car', 'cars', 'automotive'],
    beauty:          ['beauty', 'hair'],
    fashion:         ['fashion'],
    food_beverage:   ['food', 'beverage', 'food & beverage', 'F&B'],
    home:            ['home', 'house', 'interior', 'health', 'FMCG', 'consumer', 'banking', 'finance' ],
    tech:            ['tech', 'technology'],
    aviation:        ['aviation', 'airline', 'airline', 'flight'],
    travel:          ['travel', 'tourism', 'trip', 'holiday'],
    comedy:          ['comedy', 'funny', 'humour', 'humor'],
    sport:           ['sport', 'sports', 'game', 'games', 'football', 'rugby', 'world cup', 'gambling', 'superbowl'],
  }

export default function MainApp() {
  const [allProjects, setAllProjects] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [isPhoneMenuOpen, setIsPhoneMenuOpen] = useState(false);
  const [isTreatmentView, setIsTreatmentView] = useState(false);
  const [uiReady, setUiReady] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const projectsGridRef = useRef(null);

  const apiBase =
    window.location.hostname === 'localhost'
      ? 'http://localhost:8000/gateway.php'
      : window.location.origin + '/backend/public/gateway.php';

  // 1) Check admin status on mount
  useEffect(() => {
    fetch(`${apiBase}?action=checkAuth`, {
      credentials: 'include'
    })
      .then(r => r.json())
      .then(d => setIsAdmin(!!d.isAdmin))
      .catch(console.error);
  }, [apiBase]);

  // 2) Function to (re)load projects
  const fetchProjects = useCallback(() => {
    const url = `${apiBase}?action=getProjects`;
    fetch(url, { credentials: 'include' })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        setAllProjects(data);
        setLoaded(true);
      })
      .catch(err => {
        setError(err.message);
        setLoaded(true);
      });
  }, [apiBase]);

  // 3) Initial load
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // 4) Listen for AdminPanel updates
  useEffect(() => {
    const bc = new BroadcastChannel('projects');
    bc.onmessage = () => {
      fetchProjects();
    };
    return () => bc.close();
  }, [fetchProjects]);

  // 5) Fade UI in when loaded
  useEffect(() => {
    if (loaded) {
      setUiReady(true);
    }
  }, [loaded]);

  // 6) Logout handler
  const handleLogout = () => {
    fetch(`${apiBase}?action=logout`, {
      method: 'POST',
      credentials: 'include'
    })
      .then(() => {
        setIsAdmin(false);
      })
      .catch(console.error);
  };

  const handleSidebarHoverProject = id =>
    projectsGridRef.current?.scrollAndFocusProject(id);

  const togglePhoneMenu = () => {
    if (isTreatmentView) {
      setIsTreatmentView(false);
    } else {
      setIsPhoneMenuOpen(open => !open);
    }
  };

  const isPhone = window.innerWidth <= 768;

  // apply both text and category filters
  const displayedProjects = allProjects.filter(p => {
    console.log('DEBUG filtering project:', p.project_name);

    // text search
    if (!p.project_name.toLowerCase().includes(searchTerm.toLowerCase())) {
      console.log('→ excluded: name does not match searchTerm:', searchTerm);
      return false;
    }

    // category filter
    if (categoryFilter) {
      // use correct DB field
      const rawCats = p.project_categories || '';
      const catsStr = rawCats.trim().toLowerCase();
      const terms = CATEGORY_TERMS[categoryFilter] || [];

      console.log(
        '   categoryFilter:', categoryFilter,
        'rawCats:', `"${rawCats}"`,
        'normalized:', `"${catsStr}"`,
        'terms:', terms
      );

      // if empty, exclude
      if (!catsStr) {
        console.log('→ excluded: no categories present');
        return false;
      }

      // include only if any term appears as substring
      const match = terms.some(term => catsStr.includes(term));
      if (!match) {
        console.log('→ excluded: none of the terms found in categories');
        return false;
      }
      console.log('✓ category match found');
    }

    return true;
  });

  return (
    <div
      className="app-wrapper"
      style={{ opacity: uiReady ? 1 : 0, transition: 'opacity 0.3s ease' }}
    >
      {/* Category dropdown above nav bar */}
      <CategorySelector
        category={categoryFilter}
        onChange={setCategoryFilter}
      />

      {isPhone ? (
        <>
          <PhoneHeader
            onMenuClick={togglePhoneMenu}
            isMenuOpen={isPhoneMenuOpen}
            isTreatmentView={isTreatmentView}
          />
          <div className="main-content">
            <section className="right-column">
              <div className="projects-scroll-container">
                {isPhoneMenuOpen ? (
                  <PhoneMenu
                    allProjects={displayedProjects}
                    error={error}
                    onHoverProject={handleSidebarHoverProject}
                    onSearchChange={setSearchTerm}
                  />
                ) : (
                  loaded && (
                    <ProjectsGrid
                      ref={projectsGridRef}
                      allProjects={displayedProjects}
                      searchTerm={searchTerm}
                      error={error}
                      isTreatmentView={isTreatmentView}
                      onTreatmentView={setIsTreatmentView}
                      isAdmin={isAdmin}
                    />
                  )
                )}
              </div>
            </section>
          </div>
        </>
      ) : (
        <>
          <div className="main-content">
            <aside className="left-column">
              <Sidebar
                allProjects={displayedProjects}
                error={error}
                onHoverProject={handleSidebarHoverProject}
                onSearchChange={setSearchTerm}
              />
            </aside>
            <section className="right-column">
              <TopNavbar />
              <div className="projects-scroll-container">
                {loaded && (
                  <ProjectsGrid
                    ref={projectsGridRef}
                    allProjects={displayedProjects}
                    searchTerm={searchTerm}
                    error={error}
                    isTreatmentView={isTreatmentView}
                    onTreatmentView={setIsTreatmentView}
                    isAdmin={isAdmin}
                  />
                )}
              </div>
            </section>
          </div>
        </>
      )}

      {/* --- Admin bottom bar on home --- */}
      {isAdmin && (
        <div className="admin-bottom-bar">
          <button
            className="admin-view-button"
            onClick={() => (window.location.href = '/admin')}
          >
            Admin View
          </button>
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      )}
    </div>
  );
}